import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

/**
 * Chart Hover Store
 *
 * Manages hover state for compound chart components, enabling cross-component
 * communication between charts and their linked DataText displays.
 *
 * When a user hovers over a chart data point, this store is updated with the
 * hovered data. DataText components linked to that chart can subscribe and
 * display the live value.
 */

interface ChartHoverData {
  /** The full data point object from the hovered chart element */
  dataPoint: Record<string, unknown> | null;
  /** Index of the hovered data point in the chart data array */
  activeIndex: number | null;
  /** Timestamp when hover occurred (for potential debouncing) */
  timestamp: number;
}

interface ChartHoverState {
  /** Map of chart ID to hover data */
  hoverData: Record<string, ChartHoverData>;
  /** Map of chart ID to fetched data array (for idle display in linked DataText) */
  chartData: Record<string, Record<string, unknown>[]>;
}

interface ChartHoverActions {
  /** Update hover data for a specific chart */
  setHoverData: (
    chartId: string,
    dataPoint: Record<string, unknown> | null,
    index: number | null
  ) => void;
  /** Clear hover data for a specific chart (on mouse leave) */
  clearHoverData: (chartId: string) => void;
  /** Get hover data for a specific chart */
  getHoverData: (chartId: string) => ChartHoverData | undefined;
  /** Check if a chart is currently being hovered */
  isChartHovered: (chartId: string) => boolean;
  /** Store fetched chart data for linked DataText idle display */
  setChartData: (chartId: string, data: Record<string, unknown>[]) => void;
}

interface ChartHoverStore extends ChartHoverState, ChartHoverActions {}

export const useChartHoverStore = create<ChartHoverStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    hoverData: {},
    chartData: {},

    // Actions
    setHoverData: (chartId, dataPoint, index) => {
      set((state) => ({
        hoverData: {
          ...state.hoverData,
          [chartId]: {
            dataPoint,
            activeIndex: index,
            timestamp: Date.now(),
          },
        },
      }));
    },

    clearHoverData: (chartId) => {
      set((state) => ({
        hoverData: {
          ...state.hoverData,
          [chartId]: {
            dataPoint: null,
            activeIndex: null,
            timestamp: Date.now(),
          },
        },
      }));
    },

    getHoverData: (chartId) => {
      return get().hoverData[chartId];
    },

    isChartHovered: (chartId) => {
      const data = get().hoverData[chartId];
      return data?.dataPoint !== null && data?.dataPoint !== undefined;
    },

    setChartData: (chartId, data) => {
      set((state) => ({
        chartData: {
          ...state.chartData,
          [chartId]: data,
        },
      }));
    },
  }))
);

// Selectors for optimized subscriptions

/**
 * Hook to subscribe to hover data for a specific chart.
 * Only re-renders when the specific chart's hover data changes.
 */
export const useChartHoverData = (chartId: string | undefined) => {
  return useChartHoverStore((state) =>
    chartId ? state.hoverData[chartId] : undefined
  );
};

/**
 * Hook to check if a specific chart is being hovered.
 */
export const useIsChartHovered = (chartId: string | undefined) => {
  return useChartHoverStore((state) => {
    if (!chartId) return false;
    const data = state.hoverData[chartId];
    return data?.dataPoint !== null && data?.dataPoint !== undefined;
  });
};

/**
 * Hook to get a chart's fetched data array.
 * Used by linked DataText components for idle (non-hover) display.
 */
export const useChartData = (chartId: string | undefined) => {
  return useChartHoverStore((state) =>
    chartId ? state.chartData[chartId] : undefined
  );
};

/**
 * Hook to get the hovered value for a specific field from a chart.
 * Used by DataText components to display live hover values.
 */
export const useChartHoverValue = (
  chartId: string | undefined,
  valueField: string
) => {
  return useChartHoverStore((state) => {
    if (!chartId) return undefined;
    const data = state.hoverData[chartId];
    if (!data?.dataPoint) return undefined;
    return data.dataPoint[valueField];
  });
};
